import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare const grecaptcha: any;

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private siteKey = environment.RACAPTCHA_SITE_KEY;
  private isLoaded = false;
  private isLoading = false;

  constructor() {}

  loadRecaptchaScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isLoaded) {
        resolve();
        return;
      }

      if (this.isLoading) {
        // Wait for existing load to complete
        const checkLoaded = setInterval(() => {
          if (this.isLoaded) {
            clearInterval(checkLoaded);
            resolve();
          }
        }, 100);
        return;
      }

      this.isLoading = true;

      // Check if script already exists
      if (document.querySelector(`script[src*="google.com/recaptcha"]`)) {
        this.waitForGrecaptcha().then(() => {
          this.isLoaded = true;
          this.isLoading = false;
          resolve();
        });
        return;
      }

      // Create and load script
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.waitForGrecaptcha().then(() => {
          this.isLoaded = true;
          this.isLoading = false;
          resolve();
        });
      };

      script.onerror = () => {
        this.isLoading = false;
        reject('Failed to load reCAPTCHA script');
      };

      document.head.appendChild(script);
    });
  }

  private waitForGrecaptcha(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof grecaptcha !== 'undefined') {
        resolve();
        return;
      }

      const checkInterval = setInterval(() => {
        if (typeof grecaptcha !== 'undefined') {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });
  }

  async executeRecaptcha(action: string): Promise<string> {
    try {
      await this.loadRecaptchaScript();
      this.hideBadge(); // Hide the badge after loading

      return new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(this.siteKey, { action })
            .then((token: string) => resolve(token))
            .catch((error: any) => reject(error));
        });
      });
    } catch (error) {
      throw new Error(`reCAPTCHA execution failed: ${error}`);
    }
  }

  private hideBadge(): void {
    // Hide the reCAPTCHA badge programmatically
    const badge = document.querySelector('.grecaptcha-badge') as HTMLElement;
    if (badge) {
      badge.style.visibility = 'hidden';
    }
  }
}
